import React from 'react';
import { slugify } from '@/utils/helpers';
import { List } from '@zopauk/react-components';
import { TextLink } from '@/components/TextLink';
import { buildModule } from '@/components/Modules';
import { isBlockquote, isParagraph, isListItem, isHeading, isLink, isList } from 'datocms-structured-text-utils';
import {
  type StructuredTextGraphQlResponseRecord as Record,
  type StructuredTextPropTypes,
  renderNodeRule,
} from 'react-datocms';

const extractText = (node: React.ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join('');
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return extractText(node.props.children);
  }
  return '';
};

// The &nbsp; characters in the DatoCMS content are artifacts from copy-pasting — they have no semantic meaning in headings
const replaceNbsp = (node: React.ReactNode): React.ReactNode => {
  if (typeof node === 'string') {
    return node.replaceAll('\u00A0', ' ');
  }
  if (Array.isArray(node)) {
    return node.map(replaceNbsp);
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node) && node.props.children != null) {
    return React.cloneElement(node, {}, replaceNbsp(node.props.children));
  }
  return node;
};

export const renderBlock: NonNullable<StructuredTextPropTypes<Record, Record>['renderBlock']> = ({ record }) =>
  buildModule<Record>({ module: record });

/**
 * Anchor: <a />
 */
export const anchorRule = renderNodeRule(isLink, ({ children, node, key }) => {
  const props = node.meta?.reduce<{ [key: string]: string }>((acc, current) => {
    acc[current.id] = current.value;
    return acc;
  }, {});

  return (
    <TextLink {...props} href={node.url} key={key}>
      {children}
    </TextLink>
  );
});

const headingStyles: { [key: string]: string } = {
  display: 'text-7xl lg:text-8xl mb-5 block font-serif font-semibold',
  h2: 'text-4xl lg:text-5xl mb-6 mt-7 font-serif font-semibold',
  h3: 'text-2xl lg:text-3xl mb-5 font-serif font-semibold',
  h1: 'text-5xl lg:text-7xl mb-8 font-serif font-semibold',
  h4: 'text-xl lg:text-2xl my-4 font-serif font-semibold',
  h5: 'text-base lg:text-lg mb-4 font-semibold',
  h6: 'text-sm lg:text-base mb-4 font-semibold',
};

/**
 * Heading: <h? />
 */
export const headingRule = renderNodeRule(isHeading, ({ children, node, key }) => {
  const Tag = `h${node.level}` as React.ElementType;
  const text = extractText(children);
  const id = text ? slugify(text) : undefined;

  let className = headingStyles[Tag as string];

  if (node.style === 'display') {
    className = headingStyles.display;
  }

  return (
    <Tag className={className} key={key} id={id}>
      {replaceNbsp(children)}
    </Tag>
  );
});

/**
 * Paragraph: <p /> & <blockquote />
 */
export const paragraphRule = renderNodeRule(isParagraph, ({ ancestors, children, node, key }) => {
  if (node.style === 'blockquote') {
    return (
      <blockquote className="border-l-4 border-secondary pl-4 italic mb-6" key={key}>
        {children}
      </blockquote>
    );
  }

  let className = ancestors.find((node) => node.type === 'listItem') ? '' : 'mb-6';

  if (node.style === 'lead') {
    className += ' text-lg';
  }

  return (
    <p className={className} key={key}>
      {children}
    </p>
  );
});

/**
 * List: <ul /> | <ol />
 */
export const listRule = renderNodeRule(isList, ({ children, node, key }) => (
  <List as={node.style === 'numbered' ? 'ol' : 'ul'} className="mb-6" key={key}>
    {children}
  </List>
));

export const plainListRule = renderNodeRule(isList, ({ children, key }) => (
  <ul className="mb-6 list-none p-0" key={key}>
    {children}
  </ul>
));

/**
 * List Item with Icon: <li />
 */
export const listItemIconRule = (icon: string) =>
  renderNodeRule(isListItem, ({ children, key }) => (
    <li className="mb-2 flex items-start gap-2 relative has-icon" key={key}>
      <span className="material-symbols-outlined text-secondary leading-none absolute left-[-28px]" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </li>
  ));

export const listItemIconInlineRule = (icon: string) =>
  renderNodeRule(isListItem, ({ children, key }) => (
    <li className="mb-4 flex items-start gap-4 has-icon" key={key}>
      <span className="material-symbols-outlined leading-none shrink-0 mt-0.5" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </li>
  ));

/**
 * List item: <li />
 */
export const listItemRule = renderNodeRule(isListItem, ({ children, key }) => (
  <List.Item className="mb-2" key={key}>
    {children}
  </List.Item>
));

export const blockquoteRule = renderNodeRule(isBlockquote, ({ children, node, key }) => (
  <blockquote className="border-l-4 border-secondary pl-4 italic mb-6" key={key}>
    {children}
    {node.attribution ? <footer className="mt-2 not-italic font-semibold text-sm">{node.attribution}</footer> : null}
  </blockquote>
));

export const customNodeRules: StructuredTextPropTypes<Record, Record>['customNodeRules'] = [
  anchorRule,
  headingRule,
  paragraphRule,
  blockquoteRule,
  listRule,
  listItemRule,
];
